// Zod validation schemas for the multi-step indication form.
//
// Coercion strategy for numeric inputs:
//   Our form state stores numbers as `number | undefined` (e.g. CurrencyField
//   calls `onValueChange` with a parsed number; empty input leaves the field
//   undefined). That's already the right shape, so the schemas below take
//   `number` directly rather than `z.coerce.number()`. The one exception is
//   the plain integer TextField inputs (numberOfBuildings / numberOfStories /
//   squareFootage / numberOfUnits), which we read via `valueAsNumber` from
//   the DOM event handler and store as `number | undefined` as well. NaN is
//   normalised to undefined at the state-setter level.

import { z } from 'zod';
import type { IndicationInput } from '@zg/shared';
import { DEFAULT_RATE_CONFIG } from '@zg/shared';

// --- shared field schemas --------------------------------------------------

const requiredString = (label: string, min = 1) =>
  z
    .string({ required_error: `${label} is required` })
    .trim()
    .min(min, min === 1 ? `${label} is required` : `${label} must be at least ${min} characters`);

const requiredPositiveInt = (label: string) =>
  z
    .number({
      required_error: `${label} is required`,
      invalid_type_error: `${label} is required`,
    })
    .int(`${label} must be a whole number`)
    .min(1, `${label} must be at least 1`);

const requiredMoney = (label: string, min = 0) =>
  z
    .number({
      required_error: `${label} is required`,
      invalid_type_error: `${label} is required`,
    })
    .min(min, min === 0 ? `${label} must be 0 or greater` : `${label} must be at least ${min}`);

const stateCodeSchema = z.enum(['MI', 'GA', 'FL', 'TX', 'CA', 'OTHER'], {
  required_error: 'State is required',
  invalid_type_error: 'State is required',
});

const constructionTypeSchema = z.enum(
  ['Frame', 'JoistedMasonry', 'NonCombustible', 'FireResistive'],
  { required_error: 'Construction type is required' },
);

// --- per-step schemas ------------------------------------------------------

export const stepProjectInfoSchema = z.object({
  namedInsured: requiredString('Named Insured', 3),
  projectAddress: requiredString('Project Address'),
  cityStateZip: requiredString('City, State, Zip'),
  constructionType: constructionTypeSchema,
  numberOfBuildings: requiredPositiveInt('Number of Buildings'),
  numberOfStories: requiredPositiveInt('Number of Stories'),
  squareFootage: requiredPositiveInt('Square Footage'),
  numberOfUnits: requiredPositiveInt('Number of Units'),
  state: stateCodeSchema,
  projectDescription: z.string().trim().optional(),
});

export const stepBudgetSchema = z.object({
  hardCosts: requiredMoney('Hard Costs', 1),
  softCosts: requiredMoney('Soft Costs', 0),
  lossOfBusinessIncome: z
    .number({ invalid_type_error: 'Loss of Business Income must be a number' })
    .min(0, 'Loss of Business Income must be 0 or greater')
    .optional(),
});

export const stepTermSchema = z
  .object({
    startDate: requiredString('Start Date'),
    endDate: requiredString('End Date'),
  })
  .superRefine((v, ctx) => {
    const start = Date.parse(v.startDate);
    const end = Date.parse(v.endDate);
    if (!Number.isFinite(start)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Invalid start date',
      });
      return;
    }
    if (!Number.isFinite(end)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Invalid end date',
      });
      return;
    }
    if (end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date must be after start date',
      });
      return;
    }
    const days = Math.round((end - start) / 86_400_000);
    if (days > 1825) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Project length cannot exceed 5 years (1825 days)',
      });
    }
  });

const { brAop, brFlood, brWaterDamage, oiGl, pollution } = DEFAULT_RATE_CONFIG.deductibleOptions;

export const stepDeductiblesSchema = z.object({
  deductibles: z.object({
    brAop: z.number().refine((n) => brAop.includes(n), 'Invalid BR AOP deductible'),
    brFlood: z.number().refine((n) => brFlood.includes(n), 'Invalid BR Flood deductible'),
    brWaterDamage: z
      .number()
      .refine((n) => brWaterDamage.includes(n), 'Invalid BR Water Damage deductible'),
    oiGl: z.number().refine((n) => oiGl.includes(n), 'Invalid OI GL deductible'),
    pollution: z.number().refine((n) => pollution.includes(n), 'Invalid Pollution deductible'),
  }),
});

// --- combined full-input schema -------------------------------------------

export const indicationInputSchema: z.ZodType<IndicationInput> = z
  .object({
    namedInsured: requiredString('Named Insured', 3),
    projectAddress: requiredString('Project Address'),
    cityStateZip: requiredString('City, State, Zip'),
    constructionType: constructionTypeSchema,
    numberOfBuildings: requiredPositiveInt('Number of Buildings'),
    numberOfStories: requiredPositiveInt('Number of Stories'),
    squareFootage: requiredPositiveInt('Square Footage'),
    numberOfUnits: requiredPositiveInt('Number of Units'),
    state: stateCodeSchema,
    projectDescription: z.string().trim().optional(),
    hardCosts: requiredMoney('Hard Costs', 1),
    softCosts: requiredMoney('Soft Costs', 0),
    lossOfBusinessIncome: z.number().min(0).optional(),
    startDate: requiredString('Start Date'),
    endDate: requiredString('End Date'),
    deductibles: z.object({
      brAop: z.number(),
      brFlood: z.number(),
      brWaterDamage: z.number(),
      oiGl: z.number(),
      pollution: z.number(),
    }),
  })
  .superRefine((v, ctx) => {
    const start = Date.parse(v.startDate);
    const end = Date.parse(v.endDate);
    if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date must be after start date',
      });
    }
  });

export type StepKey = 'projectInfo' | 'budget' | 'term' | 'deductibles';
